import { useState } from "react";

const phases = [
  {
    step: "01",
    title: "設計定稿",
    desc: "建築師完成外觀、平面、結構設計，並確認預製模組規格",
    duration: "2–4 個月",
    icon: "ri-draft-line",
  },
  {
    step: "02",
    title: "工廠預製",
    desc: "鋼材切割、焊接、牆板複合、管線預埋，全程品檢追蹤",
    duration: "2–3 個月",
    icon: "ri-building-4-line",
    highlight: true,
  },
  {
    step: "03",
    title: "基礎施工",
    desc: "現場基礎開挖、灌漿，與工廠預製同步進行，縮短總工期",
    duration: "1–2 個月",
    icon: "ri-hammer-line",
  },
  {
    step: "04",
    title: "現場組裝",
    desc: "模組吊裝、螺栓鎖固、接縫處理，每單元 1–2 天完成",
    duration: "1–2 個月",
    icon: "ri-building-3-line",
    highlight: true,
  },
  {
    step: "05",
    title: "裝修收尾",
    desc: "外牆飾面、門窗安裝、室內裝修、軟裝配置",
    duration: "2–3 個月",
    icon: "ri-paint-brush-line",
  },
];

export default function ProcessFlowSection() {
  const [activeStep, setActiveStep] = useState<number>(1);

  return (
    <section className="py-14 md:py-20 bg-cream">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="tag-label mb-3 inline-block">CONSTRUCTION FLOW</span>
          <h2 className="mb-3">預製工法施工流程</h2>
          <p className="text-medium-gray max-w-2xl mx-auto">
            從設計到入住的完整路徑，了解預製工法如何透過「設計工廠化 + 施工現場化」縮短總工期。
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden md:block max-w-5xl mx-auto">
          <div className="relative">
            {/* Connector Line */}
            <div className="absolute top-8 left-[10%] right-[10%] h-0.5 bg-warm-gray/30" />

            <div className="grid grid-cols-5 gap-4 relative">
              {phases.map((phase, idx) => {
                const isActive = activeStep === idx;
                return (
                  <div
                    key={phase.step}
                    className="text-center cursor-pointer group"
                    onClick={() => setActiveStep(idx)}
                  >
                    {/* Circle */}
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 border-2 ${
                      isActive
                        ? "bg-champagne border-champagne shadow-lg scale-110"
                        : "bg-white border-warm-gray/30 group-hover:border-champagne/50 group-hover:shadow-md"
                    }`}>
                      <i className={`${phase.icon} text-xl ${isActive ? "text-graphite" : "text-champagne"}`} />
                    </div>
                    {/* Step badge */}
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-2 ${
                      isActive ? "bg-champagne text-graphite" : "bg-cream text-medium-gray"
                    }`}>
                      STEP {phase.step}
                    </span>
                    <h4 className={`text-base font-bold mb-1 transition-colors ${isActive ? "text-graphite" : "text-graphite group-hover:text-champagne"}`}>
                      {phase.title}
                    </h4>
                    <p className="text-xs text-medium-gray mb-1">{phase.duration}</p>
                    <div className={`overflow-hidden transition-all duration-400 ${isActive ? "max-h-24 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="text-sm text-medium-gray leading-relaxed px-1">{phase.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3 max-w-lg mx-auto">
          {phases.map((phase, idx) => {
            const isActive = activeStep === idx;
            return (
              <button
                key={phase.step}
                onClick={() => setActiveStep(idx)}
                className={`w-full text-left bg-white rounded-xl border transition-all duration-300 p-4 ${
                  isActive ? "border-champagne/40 shadow-md" : "border-warm-gray/20"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                    isActive ? "bg-champagne text-graphite" : "bg-champagne/10 text-champagne"
                  }`}>
                    <i className={`${phase.icon} text-lg`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-champagne">STEP {phase.step}</span>
                      <span className="text-xs text-medium-gray">{phase.duration}</span>
                    </div>
                    <h4 className="text-base font-bold text-graphite">{phase.title}</h4>
                  </div>
                  <span className="w-7 h-7 rounded-full bg-champagne/10 flex items-center justify-center shrink-0">
                    <i className={`ri-${isActive ? "subtract" : "add"}-line text-champagne text-sm`} />
                  </span>
                </div>
                <div className={`overflow-hidden transition-all duration-400 ${isActive ? "max-h-32 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
                  <p className="text-sm text-medium-gray leading-relaxed pl-[60px]">{phase.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Highlight Note */}
        <div className="mt-10 max-w-2xl mx-auto bg-champagne/10 border border-champagne/20 rounded-xl p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-champagne flex items-center justify-center">
              <i className="ri-lightbulb-flash-line text-graphite text-xs" />
            </span>
            <span className="text-sm font-semibold text-graphite">核心優勢</span>
          </div>
          <p className="text-sm text-medium-gray leading-relaxed">
            步驟 02（工廠預製）與步驟 03（基礎施工）可<span className="text-graphite font-semibold">同步進行</span>，這是傳統 RC 無法做到的。當現場還在挖基礎時，建築已經在工廠生產中。
          </p>
        </div>
      </div>
    </section>
  );
}